import { useEffect, useState } from "react";
import { useUserViewModel } from "../../viewmodels/user/userViewModel";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import CompanySelector from "../../components/common/CompanySelector";

const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { getUserById, updateUser, loading, roles } =
    useUserViewModel();

  const [form, setForm] = useState<any>(null);

  // 🔹 LOAD USER
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      try {
        const data = await getUserById(id);
        setForm(data);
      } catch (err: any) {
        toast.error(err.message || "Failed to load user");
        navigate("/users");
      }
    };

    load();
  }, [id]);

  // 🔹 SUBMIT
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    try {
      await updateUser(form);
      toast.success("User updated successfully");
      navigate("/users");
    } catch (err: any) {
      toast.error(err.message || "Update failed");
    }
  };

  if (loading || !form) {
    return (
      <DashboardLayout>
        <div className="p-6">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto">

        {/* BREADCRUMB */}
        <div className="text-sm text-gray-500 mb-3">
          Dashboard / Users / Edit
        </div>

        {/* HEADER */}
        <h2 className="text-xl font-semibold mb-6">
          Edit User
        </h2>

        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow rounded-xl p-6 space-y-6"
        >

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* FIRST NAME */}
            <div>
              <label className="text-sm font-medium">
                First Name
              </label>
              <input
                value={form.firstName}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
              />
            </div>

            {/* LAST NAME */}
            <div>
              <label className="text-sm font-medium">
                Last Name
              </label>
              <input
                value={form.lastName}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, lastName: e.target.value })
                }
              />
            </div>

            {/* EMAIL (READ ONLY) */}
            <div>
              <label className="text-sm font-medium">
                Email
              </label>
              <input
                value={form.email}
                readOnly
                className="w-full border rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            {/* PHONE */}
            <div>
              <label className="text-sm font-medium">
                Phone
              </label>
              <input
                value={form.phoneNumber}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({
                    ...form,
                    phoneNumber: e.target.value,
                  })
                }
              />
            </div>

            {/* ROLE */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium">
                Role *
              </label>
              <select
                value={form.roleId}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, roleId: e.target.value })
                }
              >
                <option value="">Select Role</option>
                {roles.map((r: any) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            {/* PASSWORD */}
            <div>
              <label className="text-sm font-medium">
                New Password
              </label>
              <input
                type="password"
                placeholder="Leave empty to keep current"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
              />
            </div>

            {/* CONFIRM PASSWORD */}
            <div>
              <label className="text-sm font-medium">
                Confirm Password
              </label>
              <input
                type="password"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({
                    ...form,
                    confirmPassword: e.target.value,
                  })
                }
              />
            </div>

            {/* COMPANY SELECTOR */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium">
                Companies
              </label>

              <CompanySelector
                value={form.userTenants}
                onChange={(val: any) =>
                  setForm({ ...form, userTenants: val })
                }
              />
            </div>

          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate("/users")}
              className="px-4 py-2 border rounded-lg"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow"
            >
              Update User
            </button>
          </div>

        </form>
      </div>
    </DashboardLayout>
  );
};

export default EditUserPage;